import { supabase } from "../../lib/supabase";
import {
  cacheRemoteBookmarks,
  listLocalBookmarks,
  resolveBookmarkMutation,
  type Bookmark,
  type RemoteBookmarkState,
} from "./local";
import type { Salida } from "./model";

const PAGE_SIZE = 200;
const BOOKMARK_SYNC_BATCH = 50;

export type BookmarkSyncOutcome = "synced" | "activity-pending" | "conflict-remote-wins" | "failed";
export type BookmarkSyncReport = {
  synced: number;
  pending: number;
  conflicts: number;
  failed: number;
  outcomes: Record<string, BookmarkSyncOutcome>;
};

type MutationRow = {
  status: "created" | "applied" | "noop" | "activity_pending" | "conflict" | "gone";
  activity_key: string;
  saved: boolean | null;
  version: number | null;
  updated_at: string | null;
};

async function clientFor(ownerId: string) {
  if (!supabase) throw new Error("Supabase no esta configurado.");
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error;
  if (!data.user || data.user.id !== ownerId) throw new Error("La sesion cambio; vuelve a iniciar sesion.");
  return supabase;
}

function parseRemoteState(row: Pick<MutationRow, "activity_key" | "saved" | "version" | "updated_at">): RemoteBookmarkState | null {
  if (typeof row.saved !== "boolean" || !Number.isFinite(row.version)) return null;
  const updatedAt = row.updated_at ? Date.parse(row.updated_at) : Date.now();
  return {
    activityId: row.activity_key,
    saved: row.saved,
    serverVersion: Number(row.version),
    updatedAt: Number.isFinite(updatedAt) ? updatedAt : Date.now(),
  };
}

/** Reads every metadata page. GPS is fetched separately only for visible tracks. */
export async function listRemoteActivities(ownerId: string): Promise<Salida[]> {
  const db = await clientFor(ownerId);
  const result: Salida[] = [];
  for (let offset = 0; ; offset += PAGE_SIZE) {
    const { data, error } = await db.from("activities")
      .select("id,owner_id,client_activity_id,route_id,kind,started_at,finished_at,duration_seconds,distance_m,ascent_m,points_count,updated_at")
      .eq("owner_id", ownerId)
      .not("finished_at", "is", null)
      .order("started_at", { ascending: false })
      .order("id", { ascending: false })
      .range(offset, offset + PAGE_SIZE - 1);
    if (error) throw error;
    for (const row of data ?? []) {
      if (row.owner_id !== ownerId || !row.finished_at) continue;
      const startedAt = Date.parse(row.started_at);
      const finishedAt = Date.parse(row.finished_at);
      if (!Number.isFinite(startedAt) || !Number.isFinite(finishedAt)) continue;
      result.push({
        id: row.client_activity_id ?? row.id,
        remoteId: row.id,
        ownerId,
        routeId: row.route_id,
        name: "Mi recorrido",
        kind: row.kind,
        startedAt,
        finishedAt,
        distanceM: row.distance_m ?? 0,
        durationS: row.duration_seconds ?? 0,
        ascentM: row.ascent_m ?? 0,
        pointsCount: row.points_count ?? 0,
        syncStatus: "synced",
        source: "remote",
      });
    }
    if ((data?.length ?? 0) < PAGE_SIZE) return result;
  }
}

/**
 * Includes saved rows AND server tombstones (saved=false).
 * Tombstones plus version checks stop an old offline device from silently
 * resurrecting a bookmark removed on a different device.
 */
export async function listRemoteBookmarks(ownerId: string): Promise<Map<string, RemoteBookmarkState>> {
  const db = await clientFor(ownerId);
  const states = new Map<string, RemoteBookmarkState>();
  for (let offset = 0; ; offset += PAGE_SIZE) {
    const { data, error } = await db.from("personal_map_entries")
      .select("owner_id,activity_key,saved,version,updated_at")
      .eq("owner_id", ownerId)
      .order("activity_key", { ascending: true })
      .range(offset, offset + PAGE_SIZE - 1);
    if (error) throw error;
    for (const row of data ?? []) {
      if (row.owner_id !== ownerId || !Number.isFinite(row.version)) continue;
      const updatedAt = Date.parse(row.updated_at);
      states.set(row.activity_key, {
        activityId: row.activity_key,
        saved: Boolean(row.saved),
        serverVersion: Number(row.version),
        updatedAt: Number.isFinite(updatedAt) ? updatedAt : Date.now(),
      });
    }
    if ((data?.length ?? 0) < PAGE_SIZE) return states;
  }
}

export async function getRemoteTracks(ownerId: string, remoteIds: string[]): Promise<Map<string, unknown>> {
  const result = new Map<string, unknown>();
  if (!remoteIds.length) return result;
  const db = await clientFor(ownerId);
  for (let i = 0; i < remoteIds.length; i += 20) {
    const { data, error } = await db.from("activities")
      .select("id,owner_id,track_geojson")
      .eq("owner_id", ownerId)
      .in("id", remoteIds.slice(i, i + 20));
    if (error) throw error;
    for (const row of data ?? []) if (row.owner_id === ownerId) result.set(row.id, row.track_geojson);
  }
  return result;
}

type Client = Awaited<ReturnType<typeof clientFor>>;

async function applyBookmarkMutation(db: Client, item: Bookmark): Promise<MutationRow> {
  const { data, error } = await db.rpc("apply_personal_map_entry_v1", {
    p_activity_key: item.activityId,
    p_saved: item.saved,
    p_expected_version: item.serverVersion,
  });
  if (error) throw error;
  const row = (Array.isArray(data) ? data[0] : data) as MutationRow | null;
  if (!row || row.activity_key !== item.activityId || typeof row.status !== "string") {
    throw new Error("Respuesta invalida al sincronizar Mi mapa.");
  }
  return row;
}

/**
 * Synchronizes ONLY Mi mapa bookmarks. It never calls THERA activity sync.
 * Each mutation is isolated: one failure does not block later bookmarks.
 */
export async function sendBookmarkChanges(ownerId: string): Promise<BookmarkSyncReport> {
  const report: BookmarkSyncReport = { synced: 0, pending: 0, conflicts: 0, failed: 0, outcomes: {} };
  const bookmarks = (await listLocalBookmarks(ownerId))
    .filter((item) => item.syncStatus === "pending")
    .sort((a, b) => a.updatedAt - b.updatedAt)
    .slice(0, BOOKMARK_SYNC_BATCH);
  if (!bookmarks.length) return report;

  // Fail once for an invalid/switched session, but isolate per-bookmark server/network failures.
  const db = await clientFor(ownerId);

  for (const item of bookmarks) {
    try {
      const row = await applyBookmarkMutation(db, item);
      if (row.status === "activity_pending") {
        report.pending += 1;
        report.outcomes[item.activityId] = "activity-pending";
        continue;
      }

      if (row.status === "conflict") {
        const remote = parseRemoteState(row);
        if (!remote) throw new Error("Conflicto remoto sin estado valido.");
        await resolveBookmarkMutation(ownerId, item.activityId, item.updatedAt, remote);
        report.conflicts += 1;
        report.outcomes[item.activityId] = "conflict-remote-wins";
        continue;
      }

      if (row.status === "gone" || row.status === "noop") {
        await resolveBookmarkMutation(ownerId, item.activityId, item.updatedAt, null);
        report.synced += 1;
        report.outcomes[item.activityId] = "synced";
        continue;
      }

      const remote = parseRemoteState(row);
      if (!remote) throw new Error("Sincronizacion aplicada sin version remota valida.");
      await resolveBookmarkMutation(ownerId, item.activityId, item.updatedAt, remote);
      report.synced += 1;
      report.outcomes[item.activityId] = "synced";
    } catch {
      // Keep the exact mutation pending and continue with the next one.
      report.failed += 1;
      report.outcomes[item.activityId] = "failed";
    }
  }

  return report;
}

export async function refreshBookmarks(ownerId: string): Promise<{
  saved: Set<string>;
  local: Bookmark[];
  sync: BookmarkSyncReport;
}> {
  const sync = await sendBookmarkChanges(ownerId);
  const remote = await listRemoteBookmarks(ownerId);
  await cacheRemoteBookmarks(ownerId, remote);
  const local = await listLocalBookmarks(ownerId);
  const saved = new Set<string>();
  for (const state of remote.values()) if (state.saved) saved.add(state.activityId);
  for (const item of local) {
    if (item.syncStatus !== "pending") continue;
    if (item.saved) saved.add(item.activityId);
    else saved.delete(item.activityId);
  }
  return { saved, local, sync };
}
